import React, { useCallback, useEffect, useState } from "react";
import { Client } from "@/types";
import { apiClient } from "@/services/apiClient";
import { useAppStore } from "@/core/store/useAppStore";
import { SpinnerIcon, RefreshIcon, BoltIcon } from "@/components/icons";

interface PushSubscriptionRow {
  id: string;
  userAgent: string | null;
  status: "active" | "dead";
  lastSuccessAt: string | null;
  createdAt: string;
}

interface PushDeliveryRow {
  id: string;
  trigger: string;
  status: "sent" | "failed" | "no_subscription";
  error: string | null;
  createdAt: string;
}

interface PushHealthResponse {
  subscriptions: PushSubscriptionRow[];
  deliveries: PushDeliveryRow[];
}

interface PushTestResult {
  sent: number;
  failed: number;
  noSubscription: number;
}

interface PushHealthSectionProps {
  client: Client;
}

const STATUS_STYLES: Record<string, string> = {
  active: "bg-green-50 text-green-700",
  dead: "bg-gray-100 text-gray-500",
  sent: "bg-green-50 text-green-700",
  failed: "bg-red-50 text-red-700",
  no_subscription: "bg-amber-50 text-amber-700",
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const formatDateTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const PushHealthSection: React.FC<PushHealthSectionProps> = ({
  client,
}) => {
  const addToast = useAppStore((state) => state.addToast);

  const [subscriptions, setSubscriptions] = useState<PushSubscriptionRow[]>([]);
  const [deliveries, setDeliveries] = useState<PushDeliveryRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await apiClient.get<PushHealthResponse>(
        `/api/clients/${client.id}/push/health`,
      );
      setSubscriptions(data.subscriptions ?? []);
      setDeliveries(data.deliveries ?? []);
    } catch (err) {
      setLoadError(
        err instanceof Error
          ? err.message
          : "Impossibile caricare lo stato delle notifiche push.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  const handleTest = async () => {
    setIsTesting(true);
    try {
      const result = await apiClient.post<PushTestResult>(
        `/api/clients/${client.id}/push/test`,
      );
      addToast({
        message:
          result.sent > 0
            ? `Notifica di test inviata a ${result.sent} dispositivi`
            : "Nessun dispositivo raggiunto",
        type: result.sent > 0 ? "success" : "error",
        duration: 4000,
      });
      await loadHealth();
    } catch (err) {
      addToast({
        message:
          err instanceof Error
            ? err.message
            : "Impossibile inviare la notifica di test.",
        type: "error",
        duration: 5000,
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleResend = async (deliveryId: string) => {
    setResendingId(deliveryId);
    try {
      await apiClient.post<PushTestResult>(
        `/api/push/deliveries/${deliveryId}/resend`,
      );
      addToast({
        message: "Notifica reinviata",
        type: "success",
        duration: 3000,
      });
      await loadHealth();
    } catch (err) {
      const message =
        err instanceof Error && err.message === "no_active_devices"
          ? "Nessun dispositivo attivo per il reinvio"
          : err instanceof Error
            ? err.message
            : "Impossibile reinviare la notifica.";
      addToast({ message, type: "error", duration: 5000 });
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Push notifications
        </h3>
        <button
          type="button"
          onClick={handleTest}
          disabled={isTesting}
          className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400"
        >
          {isTesting ? (
            <SpinnerIcon className="w-4 h-4" />
          ) : (
            <BoltIcon className="w-4 h-4" />
          )}
          Send test push
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <SpinnerIcon className="w-6 h-6 text-gray-400" />
        </div>
      ) : loadError ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {loadError}
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              Devices
            </p>
            {subscriptions.length === 0 ? (
              <p className="text-sm text-gray-500">
                No devices subscribed — the client has not enabled
                notifications.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {subscriptions.map((sub) => (
                  <li
                    key={sub.id}
                    className="py-2 flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-gray-700 truncate">
                      {sub.userAgent || "Unknown device"}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      {sub.lastSuccessAt && (
                        <span className="text-xs text-gray-400">
                          last ok {formatDate(sub.lastSuccessAt)}
                        </span>
                      )}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[sub.status]}`}
                      >
                        {sub.status}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              Recent deliveries
            </p>
            {deliveries.length === 0 ? (
              <p className="text-sm text-gray-500">No deliveries yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {deliveries.map((delivery) => (
                  <li
                    key={delivery.id}
                    className="py-2 flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-gray-700">
                      {delivery.trigger}
                      <span className="text-xs text-gray-400 ml-2">
                        {formatDateTime(delivery.createdAt)}
                      </span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[delivery.status]}`}
                      >
                        {delivery.status}
                      </span>
                      {delivery.status === "failed" && (
                        <button
                          type="button"
                          onClick={() => handleResend(delivery.id)}
                          disabled={resendingId === delivery.id}
                          aria-label="Resend"
                          className="text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                        >
                          {resendingId === delivery.id ? (
                            <SpinnerIcon className="w-4 h-4" />
                          ) : (
                            <RefreshIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};
